/**
 * TenantSelector
 * Searchable list for picking a tenant (hyresgäst). Same behaviour and look as
 * ExternalUserSelector: a search field that opens a list, a "Lägg till ny
 * hyresgäst" link at the bottom, and the chosen tenant as a card with "Ändra".
 */
import React, { useMemo, useState } from 'react';
import { Badge, Button, Form, ListGroup } from 'react-bootstrap';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Building, Mail, Phone, Plus, Search, User } from 'lucide-react';
import { Tenant } from '../../types/tenants';
import { TenantKind } from '../../types/Enums';

interface TenantSelectorProps {
    tenants: Tenant[];
    selectedTenant: Tenant | null;
    onSelect: (tenant: Tenant | null) => void;
    onCreateNew: () => void;
    /** Short badge text per tenant key, e.g. "Står i kö". Informational only; the tenant stays selectable. */
    badges?: { [tenantKey: string]: string };
}

const contactLine = (tenant: Tenant) => tenant.email || tenant.phone || '';

const TenantIcon: React.FC<{ tenant: Tenant; size: number }> = ({ tenant, size }) =>
    tenant.kind === TenantKind.Company ? <Building size={size} /> : <User size={size} />;

const TenantSelector: React.FC<TenantSelectorProps> = ({ tenants, selectedTenant, onSelect, onCreateNew, badges = {} }) => {
    const term = useTenantTerm();
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return tenants;
        return tenants.filter(t =>
            t.name.toLowerCase().includes(q) ||
            (t.email || '').toLowerCase().includes(q) ||
            (t.phone || '').toLowerCase().includes(q) ||
            (t.orgNumber || '').toLowerCase().includes(q)
        );
    }, [tenants, search]);

    const select = (tenant: Tenant) => {
        onSelect(tenant);
        setSearch('');
        setIsOpen(false);
    };

    if (selectedTenant) {
        return (
            <div className="p-3 border rounded bg-light d-flex align-items-center justify-content-between">
                <div>
                    <div className="d-flex align-items-center gap-2">
                        <TenantIcon tenant={selectedTenant} size={18} />
                        <strong>{selectedTenant.name}</strong>
                        {badges[selectedTenant.key] && <Badge bg="secondary">{badges[selectedTenant.key]}</Badge>}
                    </div>
                    {(selectedTenant.email || selectedTenant.phone) && (
                        <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                            {selectedTenant.email ? <Mail size={12} /> : <Phone size={12} />}
                            {contactLine(selectedTenant)}
                        </div>
                    )}
                </div>
                <Button variant="link" size="sm" onClick={() => onSelect(null)}>Ändra</Button>
            </div>
        );
    }

    return (
        <>
            <div className="position-relative">
                <Search size={18} className="position-absolute" style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                <Form.Control
                    type="text"
                    placeholder="Sök på namn, e-post, telefon eller org.nr..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    style={{ paddingLeft: 40 }}
                    autoFocus
                />
            </div>
            {isOpen && (
                <div className="border rounded mt-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <div className="p-3 text-center text-muted">
                            {search ? `Inga ${term.plural.toLowerCase()} matchar din sökning` : `Inga ${term.plural.toLowerCase()} finns`}
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {filtered.map(tenant => (
                                <ListGroup.Item key={tenant.key} action onClick={() => select(tenant)} className="d-flex align-items-center gap-2">
                                    <TenantIcon tenant={tenant} size={16} />
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <strong>{tenant.name}</strong>
                                            {badges[tenant.key] && <Badge bg="secondary" className="ms-auto">{badges[tenant.key]}</Badge>}
                                        </div>
                                        {contactLine(tenant) && <small className="text-muted">{contactLine(tenant)}</small>}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                    <div className="border-top p-2">
                        <Button variant="link" size="sm" className="w-100 text-start" onClick={() => { setIsOpen(false); onCreateNew(); }}>
                            <Plus size={16} className="me-2" />Lägg till ny {term.singular.toLowerCase()}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default TenantSelector;
